import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRESETS = [3, 10, 25];

/**
 * Public tip jar — one-click Stripe checkout for arbitrary tip amounts.
 * Lives on the Landing page footer. Anonymous or optional email.
 */
export const TipJar = () => {
  const [amount, setAmount] = useState(10);
  const [custom, setCustom] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // null | 'success' | 'cancelled'

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const sid = q.get("tipped");
    if (q.get("tip_cancelled")) {
      setStatus("cancelled");
      return;
    }
    if (!sid) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/public-status/${sid}`);
        if (cancelled) return;
        if (r?.data?.payment_status === "paid") {
          setStatus("success");
          toast.success("💚 Thank you — your tip means everything.");
        } else if (["expired", "unpaid"].includes(r?.data?.payment_status)) {
          setStatus("cancelled");
        } else {
          setTimeout(poll, 2000);
        }
      } catch (_) {
        /* silent */
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  const activeAmount = () => {
    const c = parseFloat(custom);
    if (custom && !Number.isNaN(c) && c > 0) return c;
    return amount;
  };

  const tip = async (e) => {
    e.preventDefault();
    const amt = activeAmount();
    if (!amt || amt < 1) {
      toast.error("Tip must be at least $1.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/payments/tip", {
        origin_url: window.location.origin,
        amount: amt,
        email: email.trim() || undefined,
        message: message.trim() || undefined,
      });
      if (r?.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Couldn't start checkout. Try again.");
        setBusy(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong.";
      toast.error(typeof msg === "string" ? msg : "Couldn't start checkout.");
      setBusy(false);
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="tip-success"
        className="border border-[#a855f7]/50 bg-[#a855f7]/10 text-[#a855f7] font-mono text-sm px-4 py-5 max-w-xl mx-auto rounded text-center"
      >
        💚 Thank you — every dollar keeps MoneyHub VIP running.
      </div>
    );
  }

  return (
    <div
      data-testid="tip-jar-card"
      className="border border-[#a855f7]/40 bg-black/60 px-6 py-6 max-w-xl mx-auto rounded"
    >
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#a855f7] font-mono mb-2">
        // Support the builder
      </div>
      <div className="font-heading text-xl font-black tracking-tight mb-2">
        Tip jar · keep the lights on.
      </div>
      <p className="text-xs text-zinc-300 leading-relaxed mb-4">
        MoneyHub VIP is built by one person paying real bills. If any of the ideas,
        signals, or gigs here have helped you, a tip goes a long way.
      </p>

      <form onSubmit={tip} className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={`tip-preset-${p}`}
              onClick={() => { setAmount(p); setCustom(""); }}
              className={`px-4 py-2 text-xs font-mono font-bold tracking-wider border rounded transition-colors ${
                amount === p && !custom
                  ? "border-[#a855f7] bg-[#a855f7]/20 text-white"
                  : "border-zinc-700 text-zinc-300 hover:border-[#a855f7]/60"
              }`}
            >
              ${p}
            </button>
          ))}
          <input
            data-testid="tip-custom"
            type="number"
            min="1"
            max="500"
            step="1"
            placeholder="Custom"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            className="w-24 bg-black border border-zinc-700 focus:border-[#a855f7] outline-none px-3 py-2 text-white font-mono text-xs placeholder:text-zinc-500 rounded"
          />
        </div>
        <input
          data-testid="tip-email"
          type="email"
          autoComplete="email"
          placeholder="Email (optional — for a thank-you)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-black border border-zinc-700 focus:border-[#a855f7] outline-none px-4 py-2.5 text-white font-mono text-xs placeholder:text-zinc-500 rounded"
        />
        <input
          data-testid="tip-message"
          type="text"
          maxLength={180}
          placeholder="Message (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full bg-black border border-zinc-700 focus:border-[#a855f7] outline-none px-4 py-2.5 text-white font-mono text-xs placeholder:text-zinc-500 rounded"
        />
        <button
          data-testid="tip-submit-btn"
          type="submit"
          disabled={busy}
          className="w-full text-[10px] tracking-[0.3em] uppercase text-black bg-[#a855f7] hover:bg-[#c084fc] px-6 py-3 font-black disabled:opacity-50 transition-colors rounded"
        >
          {busy ? "Loading…" : `Send tip · $${activeAmount()}`}
        </button>
      </form>
    </div>
  );
};

export default TipJar;
